import { useCallback, useEffect, useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

export interface SortState<TField extends string> {
  sortField: TField;
  sortDirection: SortDirection;
}

export interface UsePersistedTableSortOptions<T, TField extends string> {
  storageKey: string;
  defaultSort: SortState<TField>;
  validFields: readonly TField[];
  compare: (a: T, b: T, field: TField) => number;
}

function isValidStoredField<TField extends string>(
  value: string,
  validFields: readonly TField[]
): value is TField {
  return (validFields as readonly string[]).includes(value);
}

export function readPersistedSort<TField extends string>(
  storageKey: string,
  defaults: SortState<TField>,
  validFields: readonly TField[]
): SortState<TField> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as {
      sortField?: string;
      sortDirection?: string;
      sortBy?: Array<{ sortField?: string; sortDirection?: string }>;
    };
    if (Array.isArray(parsed.sortBy) && parsed.sortBy.length > 0) {
      const first = parsed.sortBy[0];
      if (
        first?.sortField &&
        isValidStoredField(first.sortField, validFields) &&
        (first.sortDirection === "asc" || first.sortDirection === "desc")
      ) {
        return { sortField: first.sortField, sortDirection: first.sortDirection };
      }
    }
    if (
      parsed.sortField &&
      isValidStoredField(parsed.sortField, validFields) &&
      (parsed.sortDirection === "asc" || parsed.sortDirection === "desc")
    ) {
      return { sortField: parsed.sortField, sortDirection: parsed.sortDirection };
    }
  } catch {
    /* ignore corrupt or missing storage */
  }
  return defaults;
}

export function usePersistedTableSort<T, TField extends string>(
  items: T[],
  options: UsePersistedTableSortOptions<T, TField> | null
) {
  const enabled = options != null;

  const [sort, setSort] = useState<SortState<TField>>(() =>
    enabled && options
      ? readPersistedSort(options.storageKey, options.defaultSort, options.validFields)
      : ({ sortField: "_" as TField, sortDirection: "asc" } as SortState<TField>)
  );

  const { sortField, sortDirection } = sort;

  useEffect(() => {
    if (!options) return;
    try {
      localStorage.setItem(
        options.storageKey,
        JSON.stringify({ sortField, sortDirection })
      );
    } catch {
      /* quota or private mode */
    }
  }, [options?.storageKey, sortField, sortDirection]);

  /** Click header: same column toggles asc/desc; new column starts at asc. */
  const handleSortColumnClick = useCallback(
    (field: string) => {
      if (!options) return;
      if (!isValidStoredField(field, options.validFields)) return;
      setSort((prev) => {
        if (prev.sortField === field) {
          return {
            ...prev,
            sortDirection: prev.sortDirection === "asc" ? "desc" : "asc",
          };
        }
        return { sortField: field, sortDirection: "asc" };
      });
    },
    [options]
  );

  const sortedItems = useMemo(() => {
    if (!enabled || !options) return items;
    const mult = sortDirection === "asc" ? 1 : -1;
    return [...items].sort((a, b) => mult * options.compare(a, b, sortField));
  }, [enabled, items, sortField, sortDirection, options]);

  return {
    sortField,
    sortDirection,
    handleSortColumnClick,
    sortedItems,
  };
}
